import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRightLeft, Camera, CheckCircle, ChevronRight, ImagePlus, Send, UserPlus, Mail, MessageSquare, Paperclip, FileText, Pencil, Reply } from 'lucide-react';
import apiService from '../../services/apiService';
import { useI18n } from '../../utils/i18n';
import { useToast } from '../ToastProvider';
import MarkdownText from '../ui/MarkdownText';
import ListState from '../ui/ListState';
import { PRIORITY_COLORS, STATUS_COLORS } from '../ui/TicketCard';
import AbschlussModal, { useAbschluss } from '../ticketDetail/Abschluss';
import UebergabeModal, { useUebergabe } from '../ticketDetail/Uebergabe';
import { useConfirm } from '../ui/ConfirmDialog';
import { darfKommentarBearbeiten, darfAnhaengeLoeschen, darfTranskribieren } from '../../utils/ticketHistorie';
import { useFeature } from '../../utils/featuresCache';
import AudioAnhang from '../ui/AudioAnhang';
import EmailDetail from '../EmailDetail';
import MailAnhaenge, { useMailAnhangViewer } from '../ticketDetail/MailAnhaenge';
import AnhangViewer from '../ui/AnhangViewer';
import { anhangArt, istAudio, ticketAnhangUrl } from '../../utils/anhangArt';
import { verkleinereFoto } from '../../utils/bildVerkleinern';
import './mobil.css';

// Ticket auf dem Handy: nur, was man unterwegs braucht.
//
//   - lesen: Titel, Kunde, Beschreibung, Verlauf (neueste zuerst)
//   - Kommentar schreiben (mit Offline-Warteschlange wie am Arbeitsplatz, #322),
//     eigene Kommentare bearbeiten oder löschen (Regel aus utils/ticketHistorie)
//   - Bilder im eigenen Viewer (?b=<id>): wischen, löschen
//   - Foto mit der Kamera aufnehmen oder Dateien (auch Bilder) wählen
//   - ein Pool-Ticket übernehmen (mit Rückfrage), ein zugewiesenes übergeben
//     oder zurück in den Pool legen (ticketDetail/Uebergabe.js, "Nicht
//     zugewiesen", samt Planungsfragen wie am Arbeitsplatz)
//   - abschließen: derselbe Ablauf wie am Arbeitsplatz (ticketDetail/
//     Abschluss.js, Zeiten oder "Ohne Zeiten", danach "Vorgang vollständig"
//     bzw. "Folgetermin?")
//
// Planen gibt es bewusst nur in der Desktop-Ansicht (Entscheidung
// C.Firchau, 2026-10-19).

const MAX_DATEIEN = 10;               // Server: upload.array('files'), max 10
const MAX_DATEI_BYTES = 10 * 1024 * 1024; // Server: 10 MB je Datei
const BESCHREIBUNG_KURZ = 600;

const istBild = (a) => !!(a && (a.isImage || /^image\//.test(a.contentType || '')));

const Vorschaubild = ({ ticketId, anhang, onOeffnen }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let objUrl = null;
    let ab = false;
    apiService.getTicketAttachmentThumbnail(ticketId, anhang.id)
      .then((blob) => { if (ab) return; objUrl = URL.createObjectURL(blob); setUrl(objUrl); })
      .catch(() => null);
    return () => { ab = true; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [ticketId, anhang.id]);
  return (
    <button type="button" className="mobil-vorschau" onClick={() => onOeffnen(anhang)} aria-label={anhang.originalName || ''}>
      {url ? <img src={url} alt={anhang.originalName || ''} /> : <Paperclip size={20} aria-hidden="true" />}
    </button>
  );
};

const MobilTicketDetail = ({ ticketId, currentUser, onClose }) => {
  const { t, getLocale } = useI18n();
  const voicemailAiAn = useFeature('voicemailAi'); // #508
  const { pushToast } = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  // #636: Mail-Anhang im Betrachter über ?ma=1, damit die Zurück-Taste nur
  // den Betrachter schließt (wie ?b= beim Bild und ?m= bei der Mail).
  const mailAnhangViewer = useMailAnhangViewer({
    beimOeffnen: () => {
      const naechste = new URLSearchParams(searchParams);
      naechste.set('ma', '1');
      setSearchParams(naechste, { state: { ...(location.state || {}), mailAnhangAusDetail: true } });
    },
    beimSchliessen: () => {
      if (location.state?.mailAnhangAusDetail) navigate(-1);
      else {
        const naechste = new URLSearchParams(searchParams);
        naechste.delete('ma');
        setSearchParams(naechste, { replace: true });
        mailAnhangViewer.schliessen();
      }
    }
  });
  const mailAnhangImUrl = searchParams.get('ma') === '1';
  const { offen: mailAnhangOffen, schliessen: schliesseMailAnhang } = mailAnhangViewer;
  useEffect(() => {
    // Zurück-Taste: Der Eintrag ist weg, also Betrachter zu.
    if (mailAnhangOffen && !mailAnhangImUrl) schliesseMailAnhang();
  }, [mailAnhangOffen, mailAnhangImUrl, schliesseMailAnhang]);
  useEffect(() => {
    // ?ma=1 ohne offenen Betrachter (neu geladen): Parameter entfernen.
    if (mailAnhangImUrl && !mailAnhangOffen) {
      const naechste = new URLSearchParams(searchParams);
      naechste.delete('ma');
      setSearchParams(naechste, { replace: true });
    }
  }, [mailAnhangImUrl, mailAnhangOffen, searchParams, setSearchParams]);
  const [ticket, setTicket] = useState(null);
  const [verlauf, setVerlauf] = useState([]);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState(null);
  const [kommentar, setKommentar] = useState('');
  const [sendet, setSendet] = useState(false);
  const [laedtHoch, setLaedtHoch] = useState(false);
  const [uebernimmt, setUebernimmt] = useState(false);
  const [beschreibungOffen, setBeschreibungOffen] = useState(false);
  // Kommentar in Bearbeitung: { id (Zahl), text } oder null
  const [bearbeitung, setBearbeitung] = useState(null);
  const [speichert, setSpeichert] = useState(false);
  const [verfuegbareEmails, setVerfuegbareEmails] = useState([]);
  const geaendertRef = useRef(false);
  const kameraRef = useRef(null);
  const dateiRef = useRef(null);

  const laden = useCallback(async () => {
    setFehler(null);
    try {
      const [antwort, historie] = await Promise.all([
        apiService.getTicket(ticketId),
        apiService.getTicketHistory(ticketId).catch(() => null)
      ]);
      setTicket(antwort?.ticket || antwort || null);
      setVerlauf(Array.isArray(historie?.history) ? historie.history : []);
    } catch (e) {
      // Nur der Grund wird gemerkt, der Text entsteht beim Rendern. So hängt
      // laden() nicht an t(), und ein Sprachwechsel lädt nicht neu.
      setFehler(e?.response?.status === 403 ? 'verboten' : 'allgemein');
    } finally {
      setLaedt(false);
    }
  }, [ticketId]);

  useEffect(() => {
    setLaedt(true);
    laden();
    apiService.markTicketAsViewed(ticketId).catch(() => null);
  }, [ticketId, laden]);

  const schliessen = () => onClose(geaendertRef.current);

  // Abschluss wie in TicketDetail. Der Hook meldet Fehler über setError und
  // schließt am Ende mit onClose('completed'); das reicht die Ansicht an die
  // Liste weiter, die das Ticket dann sofort ausblendet.
  const abschluss = useAbschluss({
    ticket: ticket || { id: ticketId },
    currentTicket: ticket,
    setCurrentTicket: setTicket,
    currentUser,
    onClose: (grund) => onClose(grund || true),
    setError: (meldung) => { if (meldung) pushToast({ message: meldung, type: 'error' }); },
    setHasChanges: (wert) => { if (wert) geaendertRef.current = true; }
  });

  // Übergeben bzw. zurück in den Pool, wie in TicketDetail.
  const uebergabe = useUebergabe({
    ticket: ticket || { id: ticketId },
    currentTicket: ticket || {},
    onClose: (grund) => onClose(grund || true),
    onTicketUpdate: (neu) => { if (neu) setTicket(neu); },
    setError: (meldung) => { if (meldung) pushToast({ message: meldung, type: 'error' }); },
    setHasChanges: (wert) => { if (wert) geaendertRef.current = true; },
    getCurrentUserEmail: () => meineEmail
  });

  const oeffneUebergabe = async () => {
    // Die Auswahlliste lädt erst, wenn jemand sie braucht.
    try {
      const antwort = await apiService.getAvailableEmails();
      setVerfuegbareEmails(antwort?.emails || []);
    } catch (_) {
      setVerfuegbareEmails([]);
    }
    uebergabe.handleOpenTransferModal();
  };

  const meineEmail = currentUser ? (currentUser.mail || currentUser.userPrincipalName || currentUser.email || '') : '';
  const dbUser = currentUser?.dbUser || null;
  const archiviert = !!ticket?.isArchived;
  const darfSchreiben = !archiviert && !(dbUser && dbUser.isReadonly);
  const istPool = ticket && !(ticket.assignedTo || '').trim();
  const istWorker = !dbUser || dbUser.isTicketworker || dbUser.isManager;
  const darfUebernehmen = darfSchreiben && istPool && meineEmail && istWorker;
  const darfAbschliessen = darfSchreiben && istWorker && ticket && ticket.status !== 'abgeschlossen';
  const darfUebergeben = darfSchreiben && istWorker && ticket && !istPool && ticket.status !== 'abgeschlossen';
  // #508: Transkribieren an Audio-Anhängen, danach den Verlauf neu laden
  const transkribiere = darfTranskribieren({ featureAn: voicemailAiAn, ticket, dbUser })
    ? async ({ quelle, anhangId, emailId }) => {
      await apiService.transkribiereAnhang(ticket.id, { quelle, anhangId, emailId });
      await laden();
    }
    : undefined;

  const fehlerText = (e, standard) => e?.response?.data?.error || standard;

  const sendeKommentar = async (e) => {
    e.preventDefault();
    const text = kommentar.trim();
    if (!text || sendet) return;
    setSendet(true);
    try {
      // #322: selbst getippter Kommentar -- darf in die Offline-Warteschlange.
      const ergebnis = await apiService.addCommentToTicket(ticket.id, text, false, { offlineFaehig: true });
      setKommentar('');
      if (ergebnis?.eingereiht) {
        pushToast({
          message: t('ticketDetail.commentQueued', 'Kein Netz — der Kommentar ist gespeichert und wird gesendet, sobald die Verbindung zurück ist.'),
          type: 'info',
          timeoutMs: 0
        });
        return;
      }
      geaendertRef.current = true;
      pushToast({ message: t('mobile.commentSaved', 'Kommentar gespeichert.'), type: 'success' });
      await laden();
    } catch (err) {
      pushToast({ message: fehlerText(err, t('mobile.commentError', 'Der Kommentar konnte nicht gespeichert werden.')), type: 'error' });
    } finally {
      setSendet(false);
    }
  };

  const ladeHoch = async (liste) => {
    const dateien = Array.from(liste || []);
    if (dateien.length === 0) return;
    if (dateien.length > MAX_DATEIEN) {
      pushToast({ message: t('mobile.uploadTooMany', 'Höchstens {{count}} Dateien auf einmal.').replace('{{count}}', String(MAX_DATEIEN)), type: 'error' });
      return;
    }
    setLaedtHoch(true);
    try {
      const fertig = await Promise.all(dateien.map(verkleinereFoto));
      const zuGross = fertig.find(d => d.size > MAX_DATEI_BYTES);
      if (zuGross) {
        pushToast({ message: t('mobile.uploadTooLarge', '„{{name}}“ ist größer als 10 MB.').replace('{{name}}', zuGross.name || ''), type: 'error' });
        return;
      }
      await apiService.uploadTicketAttachments(ticket.id, fertig);
      geaendertRef.current = true;
      pushToast({
        message: fertig.length === 1
          ? t('mobile.uploadDoneOne', 'Datei hochgeladen.')
          : t('mobile.uploadDoneMany', '{{count}} Dateien hochgeladen.').replace('{{count}}', String(fertig.length)),
        type: 'success'
      });
      await laden();
    } catch (err) {
      pushToast({ message: fehlerText(err, t('mobile.uploadError', 'Das Hochladen ist fehlgeschlagen.')), type: 'error' });
    } finally {
      setLaedtHoch(false);
    }
  };

  const beiDateiwahl = (e) => {
    const dateien = e.target.files;
    ladeHoch(dateien);
    // Dasselbe Foto erneut wählen können
    e.target.value = '';
  };

  const uebernehmen = async () => {
    // Ein Fehltipp soll nicht sofort zuweisen.
    const ok = await confirm({
      title: t('mobile.takeOver', 'Übernehmen'),
      message: t('mobile.confirmTakeOver', 'Dieses Ticket dir zuweisen?'),
      confirmLabel: t('mobile.takeOver', 'Übernehmen')
    });
    if (!ok) return;
    setUebernimmt(true);
    try {
      const antwort = await apiService.updateTicket(ticket.id, { assignedTo: meineEmail });
      geaendertRef.current = true;
      setTicket(antwort?.ticket || { ...ticket, assignedTo: meineEmail });
      pushToast({ message: t('mobile.takenOver', 'Das Ticket ist jetzt dir zugewiesen.'), type: 'success' });
    } catch (err) {
      pushToast({ message: fehlerText(err, t('contextMenu.assignError', 'Fehler beim Zuweisen des Tickets.')), type: 'error' });
    } finally {
      setUebernimmt(false);
    }
  };

  // Bildansicht über ?b=<Anhang-ID>: Die Zurück-Taste schließt dann das Bild
  // und nicht das Ticket. Muster wie ?t= in MobilStart.
  const offenesBild = searchParams.get('b');
  const bildGruppe = useMemo(() => {
    if (!offenesBild) return null;
    const gruppe = verlauf.find(item => item.type === 'attachment'
      && (item.attachments || []).some(a => String(a.id) === String(offenesBild)));
    if (!gruppe) return null;
    // #636: Bilder UND PDFs der Gruppe im Betrachter
    const bilder = gruppe.attachments.filter(a => anhangArt(a));
    return { gruppe, bilder, start: Math.max(0, bilder.findIndex(a => String(a.id) === String(offenesBild))) };
  }, [offenesBild, verlauf]);

  // Steht ?b= in der Adresse, das Bild gibt es aber (nicht mehr), etwa
  // nach dem Löschen des letzten Bilds einer Gruppe: Parameter entfernen.
  useEffect(() => {
    if (offenesBild && !laedt && !bildGruppe) {
      const naechste = new URLSearchParams(searchParams);
      naechste.delete('b');
      setSearchParams(naechste, { replace: true });
    }
  }, [offenesBild, laedt, bildGruppe, searchParams, setSearchParams]);

  const oeffneBild = (anhang) => {
    const naechste = new URLSearchParams(searchParams);
    naechste.set('b', String(anhang.id));
    setSearchParams(naechste, { state: { ...(location.state || {}), bildAusDetail: true } });
  };

  const schliesseBild = useCallback(() => {
    if (location.state?.bildAusDetail) navigate(-1);
    else {
      const naechste = new URLSearchParams(searchParams);
      naechste.delete('b');
      setSearchParams(naechste, { replace: true });
    }
  }, [location.state, navigate, searchParams, setSearchParams]);

  // #634: Mail über ?m=<Mail-ID>, wie das Bild über ?b=. Geöffnet wird die
  // Mail-Ansicht des Arbeitsplatzes (EmailDetail), die unter 480 px ohnehin
  // Vollbild ist; damit gibt es hier dieselbe Antwort-Funktion.
  const offeneMailId = searchParams.get('m');
  const offeneMail = useMemo(() => {
    if (!offeneMailId) return null;
    const item = verlauf.find(e => e.type === 'email' && e.email && String(e.email.id) === String(offeneMailId));
    return item ? item.email : null;
  }, [offeneMailId, verlauf]);

  useEffect(() => {
    if (offeneMailId && !laedt && !offeneMail) {
      const naechste = new URLSearchParams(searchParams);
      naechste.delete('m');
      setSearchParams(naechste, { replace: true });
    }
  }, [offeneMailId, laedt, offeneMail, searchParams, setSearchParams]);

  const oeffneMail = (email) => {
    const naechste = new URLSearchParams(searchParams);
    naechste.set('m', String(email.id));
    setSearchParams(naechste, { state: { ...(location.state || {}), mailAusDetail: true } });
  };

  const schliesseMail = useCallback(() => {
    if (location.state?.mailAusDetail) navigate(-1);
    else {
      const naechste = new URLSearchParams(searchParams);
      naechste.delete('m');
      setSearchParams(naechste, { replace: true });
    }
  }, [location.state, navigate, searchParams, setSearchParams]);

  const bildGeloescht = useCallback((id) => {
    geaendertRef.current = true;
    // Sofort aus der Gruppe nehmen, damit der Viewer weiterblättert; danach
    // den Verlauf neu laden. Ist die Gruppe leer, schließt der Viewer selbst.
    setVerlauf(prev => prev
      .map(item => (item.type === 'attachment'
        ? { ...item, attachments: (item.attachments || []).filter(a => String(a.id) !== String(id)) }
        : item))
      .filter(item => item.type !== 'attachment' || (item.attachments || []).length > 0));
    pushToast({ message: t('mobile.imageDeleted', 'Bild gelöscht.'), type: 'success' });
    laden();
  }, [laden, pushToast, t]);

  const kommentarId = (item) => {
    const treffer = String(item?.id || '').match(/^comment-(\d+)/);
    return treffer ? Number(treffer[1]) : null;
  };

  const speichereKommentar = async () => {
    const text = (bearbeitung?.text || '').trim();
    if (!bearbeitung || !text) return;
    setSpeichert(true);
    try {
      await apiService.updateTicketComment(ticket.id, bearbeitung.id, text);
      geaendertRef.current = true;
      setBearbeitung(null);
      await laden();
    } catch (err) {
      pushToast({ message: fehlerText(err, t('ticketDetail.updateError', 'Fehler beim Aktualisieren des Kommentars')), type: 'error' });
    } finally {
      setSpeichert(false);
    }
  };

  const loescheKommentar = async () => {
    if (!bearbeitung) return;
    const ok = await confirm({ message: t('ticketDetail.confirmDeleteComment', 'Diesen Kommentar wirklich löschen?'), danger: true });
    if (!ok) return;
    setSpeichert(true);
    try {
      await apiService.deleteTicketComment(ticket.id, bearbeitung.id);
      geaendertRef.current = true;
      setBearbeitung(null);
      await laden();
    } catch (err) {
      pushToast({ message: fehlerText(err, t('ticketDetail.deleteError', 'Fehler beim Löschen des Kommentars')), type: 'error' });
    } finally {
      setSpeichert(false);
    }
  };

  // #636: Andere Dateien gehen per Link direkt auf die Download-Adresse,
  // in der Nutzeraktion selbst. Blob + window.open nach dem Laden blockieren
  // Handy-Browser, und der Blob hatte weder Typ noch Namen.
  const anhangImViewer = (a) => ({
    id: a.id,
    name: a.originalName,
    typ: a.contentType,
    laden: () => apiService.downloadTicketAttachment(ticket.id, a.id),
    downloadUrl: ticketAnhangUrl(ticket.id, a.id)
  });

  const zeit = (wert) => {
    try {
      return new Date(wert).toLocaleString(getLocale(), { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    } catch (_) { return ''; }
  };

  const statusText = (s) => (
    s === 'offen' ? t('ticket.status.open', 'Offen') :
    s === 'in_bearbeitung' ? t('ticket.status.in_progress', 'In Bearbeitung') :
    s === 'wartend' ? t('ticket.status.waiting', 'Wartend') :
    s === 'zugewiesen' ? t('ticket.status.assigned', 'Zugewiesen') :
    s === 'abgeschlossen' ? t('ticket.status.completed', 'Abgeschlossen') : s
  );
  const prioText = (p) => (
    p === 'kritisch' ? t('ticket.priority.critical', 'Kritisch') :
    p === 'hoch' ? t('ticket.priority.high', 'Hoch') :
    p === 'normal' ? t('ticket.priority.normal', 'Normal') :
    p === 'niedrig' ? t('ticket.priority.low', 'Niedrig') : p
  );

  const beschreibung = ticket?.description || '';
  const beschreibungLang = beschreibung.length > BESCHREIBUNG_KURZ;

  // Wie am Arbeitsplatz ohne System-Einträge: "Datei hinzugefügt" stünde
  // sonst doppelt neben dem Anhang.
  const sichtbarerVerlauf = verlauf.filter(item => item.type !== 'system');

  const eintrag = (item) => {
    const id = item.type === 'comment' ? kommentarId(item) : null;
    const bearbeitbar = darfSchreiben && id != null && darfKommentarBearbeiten(item, verlauf, meineEmail);
    const inBearbeitung = bearbeitung && id != null && bearbeitung.id === id;
    let icon = <MessageSquare size={16} aria-hidden="true" />;
    let inhalt = inBearbeitung ? (
      <div className="mobil-kommentar-edit">
        <textarea
          value={bearbeitung.text}
          onChange={(e) => setBearbeitung({ id, text: e.target.value })}
          aria-label={t('mobile.editComment', 'Kommentar bearbeiten')}
          rows={4}
        />
        <div className="mobil-kommentar-edit-knoepfe">
          <button type="button" className="btn btn-danger mobil-touch" onClick={loescheKommentar} disabled={speichert}>
            {t('common.delete', 'Löschen')}
          </button>
          <button type="button" className="btn btn-secondary mobil-touch" onClick={() => setBearbeitung(null)} disabled={speichert}>
            {t('common.cancel', 'Abbrechen')}
          </button>
          <button type="button" className="btn btn-primary mobil-touch" onClick={speichereKommentar} disabled={speichert || !bearbeitung.text.trim()}>
            {t('common.save', 'Speichern')}
          </button>
        </div>
      </div>
    ) : <MarkdownText variant="comment">{item.content || ''}</MarkdownText>;
    if (item.type === 'email') {
      icon = <Mail size={16} aria-hidden="true" />;
      const mailText = (
        <>
          <div className="mobil-verlauf-betreff">{item.email?.subject || item.content}</div>
          {item.email?.bodyPreview && <div className="mobil-verlauf-vorschau">{item.email.bodyPreview}</div>}
        </>
      );
      // Ohne gecachte Mail (alte Archiv-Verknüpfung) gibt es nichts zu öffnen.
      // v3.0.9: Als Karte mit Vorschau auf drei Zeilen und sichtbarem
      // „Öffnen und antworten“ – vorher stand die Vorschau ausgeklappt da,
      // und dass man tippen kann, sah man nicht.
      inhalt = item.email ? (
        <button type="button" className="mobil-mail-oeffnen" onClick={() => oeffneMail(item.email)}
          aria-label={t('mobile.openEmail', 'E-Mail öffnen: {{subject}}').replace('{{subject}}', item.email.subject || '')}>
          {mailText}
          <span className="mobil-mail-aktion" aria-hidden="true">
            <Reply size={16} /> {t('mobile.openAndReply', 'Öffnen und antworten')} <ChevronRight size={16} />
          </span>
        </button>
      ) : mailText;
      // #635: Anhänge außerhalb des Knopfs (ein Link im Knopf ist ungültig).
      // Sie ersetzen den Hinweis „Mit Anhang“ aus #634, der über
      // hasAttachments auch Signaturbilder mitzählte.
      if (item.mailAnhaenge && item.mailAnhaenge.length > 0) {
        inhalt = <>{inhalt}<MailAnhaenge anhaenge={item.mailAnhaenge} onOeffnen={mailAnhangViewer.oeffnenFuer(item.mailAnhaenge)}
          onTranskribieren={transkribiere ? (a) => transkribiere({ quelle: 'mail', anhangId: a.id, emailId: a.emailId }) : undefined} /></>;
      }
    } else if (item.type === 'attachment') {
      icon = <Paperclip size={16} aria-hidden="true" />;
      const anhaenge = Array.isArray(item.attachments) ? item.attachments : [];
      inhalt = (
        <div className="mobil-anhaenge">
          {anhaenge.map(a => (istBild(a)
            ? <Vorschaubild key={a.id} ticketId={ticket.id} anhang={a} onOeffnen={oeffneBild} />
            : anhangArt(a)
              ? (
                <button key={a.id} type="button" className="mobil-datei" onClick={() => oeffneBild(a)}>
                  <FileText size={16} aria-hidden="true" /> <span>{a.originalName}</span>
                </button>
              ) : istAudio(a) ? (
                // #508: Audio mit Player (und Transkribieren), über die volle Breite
                <div key={a.id} className="mobil-audio">
                  <a className="mobil-datei" href={ticketAnhangUrl(ticket.id, a.id)} download={a.originalName || true}>
                    <FileText size={16} aria-hidden="true" /> <span>{a.originalName}</span>
                  </a>
                  <AudioAnhang
                    anhang={a}
                    laden={() => apiService.downloadTicketAttachment(ticket.id, a.id)}
                    onTranskribieren={transkribiere ? () => transkribiere({ quelle: 'ticket', anhangId: a.id }) : undefined}
                  />
                </div>
              ) : (
                <a key={a.id} className="mobil-datei" href={ticketAnhangUrl(ticket.id, a.id)} download={a.originalName || true}>
                  <FileText size={16} aria-hidden="true" /> <span>{a.originalName}</span>
                </a>
              )))}
        </div>
      );
    } else if (item.type === 'teams_message') {
      inhalt = <div className="mobil-verlauf-vorschau">{item.content}</div>;
    }
    return (
      <li key={item.id} className={`mobil-verlauf-eintrag${item.isInternal ? ' intern' : ''}`}>
        <div className="mobil-verlauf-kopf">
          {icon}
          <strong>{item.authorName || ''}</strong>
          <time>{zeit(item.sortTimestamp || item.timestamp)}</time>
          {bearbeitbar && !inBearbeitung && (
            <button type="button" className="mobil-bearbeiten" onClick={() => setBearbeitung({ id, text: item.content || '' })}
              aria-label={t('mobile.editComment', 'Kommentar bearbeiten')} title={t('mobile.editComment', 'Kommentar bearbeiten')}>
              <Pencil size={18} />
            </button>
          )}
        </div>
        {inhalt}
      </li>
    );
  };

  return (
    <div className="mobil-detail" role="dialog" aria-modal="true" aria-label={ticket?.title || t('mobile.ticket', 'Ticket')}>
      <header className="mobil-kopf">
        <button type="button" className="mobil-kopf-knopf" onClick={schliessen}
          aria-label={t('mobile.back', 'Zurück')} title={t('mobile.back', 'Zurück')}>
          <ArrowLeft size={24} />
        </button>
        <h1>{ticket?.ticketNumber || t('mobile.ticket', 'Ticket')}</h1>
      </header>

      <div className="mobil-detail-inhalt">
        {laedt && <ListState variant="loading" />}
        {!laedt && fehler && (
          <ListState
            variant="error"
            message={fehler === 'verboten'
              ? t('mobile.ticketForbidden', 'Dieses Ticket ist für dich nicht sichtbar.')
              : t('mobile.ticketLoadError', 'Das Ticket konnte nicht geladen werden.')}
            onRetry={() => { setLaedt(true); laden(); }}
          />
        )}

        {!laedt && !fehler && ticket && (
          <>
            <section className="mobil-karte">
              {(ticket.customerShortName || ticket.customerName) && (
                <div className="mobil-kunde">
                  {ticket.customerShortName || ticket.customerName}
                  {ticket.contactName ? ` · ${ticket.contactName}` : ''}
                </div>
              )}
              <h2 className="mobil-titel">{ticket.title}</h2>
              <div className="mobil-chips">
                <span className="mobil-chip"><span className="mobil-punkt" style={{ background: PRIORITY_COLORS[ticket.priority] }} />{prioText(ticket.priority)}</span>
                <span className="mobil-chip"><span className="mobil-punkt" style={{ background: STATUS_COLORS[ticket.status] }} />{statusText(ticket.status)}</span>
                {archiviert && <span className="mobil-chip">{t('mobile.archived', 'Archiviert')}</span>}
              </div>
              <div className="mobil-zustaendig">
                {istPool ? t('mobile.poolTicket', 'Pool-Ticket, niemandem zugewiesen') : ticket.assignedTo}
              </div>
            </section>

            <section className="mobil-aktionen" aria-label={t('mobile.actions', 'Aktionen')}>
              {darfSchreiben && (
                <>
                  <button type="button" className="mobil-aktion" onClick={() => kameraRef.current?.click()} disabled={laedtHoch}>
                    <Camera size={24} aria-hidden="true" />
                    <span>{laedtHoch ? t('mobile.uploading', 'Lädt hoch…') : t('mobile.takePhoto', 'Foto')}</span>
                  </button>
                  <button type="button" className="mobil-aktion" onClick={() => dateiRef.current?.click()} disabled={laedtHoch}>
                    <ImagePlus size={24} aria-hidden="true" />
                    <span>{t('mobile.chooseFiles', 'Dateien')}</span>
                  </button>
                </>
              )}
              {darfUebernehmen && (
                <button type="button" className="mobil-aktion" onClick={uebernehmen} disabled={uebernimmt}>
                  <UserPlus size={24} aria-hidden="true" />
                  <span>{t('mobile.takeOver', 'Übernehmen')}</span>
                </button>
              )}
              {darfUebergeben && (
                <button type="button" className="mobil-aktion" onClick={oeffneUebergabe}>
                  <ArrowRightLeft size={24} aria-hidden="true" />
                  <span>{t('ticketDetail.transfer', 'Übergeben')}</span>
                </button>
              )}
              {darfAbschliessen && (
                <button type="button" className="mobil-aktion" onClick={abschluss.handleOpenCompleteModal}>
                  <CheckCircle size={24} aria-hidden="true" />
                  <span>{t('ticketDetail.complete', 'Abschließen')}</span>
                </button>
              )}
              {/* capture: öffnet direkt die Rückkamera statt der Galerie */}
              <input ref={kameraRef} type="file" accept="image/*" capture="environment" hidden onChange={beiDateiwahl} data-testid="mobil-kamera" />
              <input ref={dateiRef} type="file" accept="image/*,application/pdf,.msg,.eml,.txt,.doc,.docx,.xls,.xlsx" multiple hidden onChange={beiDateiwahl} data-testid="mobil-dateien" />
            </section>

            {darfSchreiben && (
              <form className="mobil-kommentar" onSubmit={sendeKommentar}>
                <label htmlFor="mobil-kommentar-text" className="mobil-label">{t('mobile.newComment', 'Kommentar')}</label>
                <textarea
                  id="mobil-kommentar-text"
                  value={kommentar}
                  onChange={(e) => setKommentar(e.target.value)}
                  rows={3}
                  placeholder={t('mobile.commentPlaceholder', 'Was hast du gemacht oder festgestellt?')}
                />
                <button type="submit" className="btn btn-primary mobil-touch mobil-senden" disabled={!kommentar.trim() || sendet}>
                  <Send size={18} aria-hidden="true" />
                  <span>{sendet ? t('mobile.sending', 'Sendet…') : t('mobile.send', 'Senden')}</span>
                </button>
              </form>
            )}

            {beschreibung && (
              <section className="mobil-karte">
                <h3 className="mobil-label">{t('ticketDetail.description', 'Beschreibung')}</h3>
                <div className="mobil-beschreibung">
                  {beschreibungLang && !beschreibungOffen ? `${beschreibung.slice(0, BESCHREIBUNG_KURZ)}…` : beschreibung}
                </div>
                {beschreibungLang && (
                  <button type="button" className="btn btn-secondary mobil-touch mobil-mehr" onClick={() => setBeschreibungOffen(o => !o)}>
                    {beschreibungOffen ? t('mobile.showLess', 'Weniger anzeigen') : t('mobile.showMore', 'Mehr anzeigen')}
                  </button>
                )}
              </section>
            )}

            <section className="mobil-karte">
              <h3 className="mobil-label">{t('mobile.history', 'Verlauf')}</h3>
              {sichtbarerVerlauf.length === 0
                ? <p className="mobil-leer">{t('mobile.historyEmpty', 'Noch keine Einträge.')}</p>
                : <ul className="mobil-verlauf">{sichtbarerVerlauf.map(eintrag)}</ul>}
            </section>
          </>
        )}
      </div>
      {mailAnhangViewer.viewer}
      {ticket && <AbschlussModal abschluss={abschluss} currentTicket={ticket} getCurrentUserEmail={() => meineEmail} />}
      {ticket && <UebergabeModal uebergabe={uebergabe} currentTicket={ticket} availableEmails={verfuegbareEmails} />}
      {ticket && offeneMail && (
        <EmailDetail email={offeneMail} onClose={schliesseMail} mobil />
      )}
      {ticket && bildGruppe && bildGruppe.bilder.length > 0 && (
        <AnhangViewer
          key={bildGruppe.gruppe.id}
          anhaenge={bildGruppe.bilder.map(anhangImViewer)}
          startIndex={bildGruppe.start}
          darfLoeschen={darfSchreiben && darfAnhaengeLoeschen(bildGruppe.gruppe, verlauf, meineEmail)}
          onLoeschen={async (a) => { await apiService.deleteTicketAttachment(ticket.id, a.id); bildGeloescht(a.id); }}
          onClose={schliesseBild}
        />
      )}
    </div>
  );
};

export default MobilTicketDetail;
